import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type User } from "../lib/api";
import { LoadingButton } from "../components/Spinner";

// Hard email-verification gate. Rendered in place of the app whenever the
// signed-in user hasn't confirmed their email yet. They can resend the
// email, re-check (refetch /me after clicking the link in another tab), or
// sign out — nothing else.
export default function VerifyGate({ user }: { user: User }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [msg, setMsg] = useState<string | null>(null);

  const resend = useMutation({
    mutationFn: () => api.resendVerification(),
    onSuccess: (r) => {
      if (r.alreadyVerified) { qc.invalidateQueries({ queryKey: ["me"] }); return; }
      setMsg(`Sent — check ${user.email} for the confirmation link.`);
    },
    onError: () => setMsg("Couldn't resend right now — try again in a minute."),
  });

  const recheck = useMutation({
    mutationFn: () => api.me(),
    onSuccess: (r) => {
      if (r.user?.emailVerified) qc.setQueryData(["me"], r);
      else setMsg("Not confirmed yet. Click the link in the email, then re-check.");
    },
  });

  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData(["me"], { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      nav("/login");
    },
  });

  return (
    <div className="relative grid h-full place-items-center bg-bg p-6 overflow-hidden">
      <div className="auth-glow" aria-hidden="true" />
      <div className="card auth-card max-w-md w-full p-8 space-y-5 relative text-center">
        <div className="text-3xl">✉️</div>
        <h1 className="text-xl font-semibold">Confirm your email</h1>
        <p className="text-sm muted">
          We sent a confirmation link to <strong className="text-text">{user.email}</strong>.
          Click it to activate your account — then come back here.
        </p>
        {msg && <p className="text-xs muted">{msg}</p>}
        <div className="space-y-2">
          <LoadingButton
            className="btn-primary w-full justify-center"
            pending={recheck.isPending}
            onClick={() => { setMsg(null); recheck.mutate(); }}
          >
            I've confirmed — continue
          </LoadingButton>
          <LoadingButton
            className="btn w-full justify-center"
            pending={resend.isPending}
            pendingLabel="Sending…"
            onClick={() => { setMsg(null); resend.mutate(); }}
          >
            Resend email
          </LoadingButton>
        </div>
        <button
          className="text-xs muted hover:text-text underline"
          onClick={() => logout.mutate()}
        >
          Sign out
        </button>
        <p className="text-[11px] muted-strong">
          Wrong address? Sign out and create an account with the right one.
        </p>
      </div>
    </div>
  );
}
